import { useState } from 'react';
import { D, formatMoney, parseMoney } from '@makila/fiscal';
import type { CashReport } from '@makila/pos-core';
import { Loader2, LockOpen } from 'lucide-react';

/** Antes da primeira venda do turno: o fundo de caixa (numerário para trocos). */
/** onKitchen: restaurante e gerente — este equipamento passa a ecrã da cozinha (não abre caixa nem vende). */
export function CashOpenScreen({ operatorName, lastZ, onOpen, onLock, onKitchen }: { operatorName: string; lastZ: CashReport | null; onOpen: (float: string) => Promise<string | null>; onLock: () => void; onKitchen?: () => void }) {
  const [v, setV] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <div className="grid h-full place-items-center p-4">
      <form className="surface w-full max-w-md rounded-2xl p-6" onSubmit={async (e) => {
        e.preventDefault();
        const f = parseMoney(v || '0');
        if (f === null) { setError('Valor inválido'); return; }
        setBusy(true);
        setError(await onOpen(f));
        setBusy(false);
      }}>
        {lastZ && <ZResult z={lastZ} />}
        <h1 className="font-display text-2xl font-bold">Abrir a caixa</h1>
        <p className="mt-1 text-muted">Olá, {operatorName}. Conte o dinheiro que fica na gaveta para trocos e escreva o total.</p>
        <label htmlFor="float" className="mt-5 block text-sm font-medium">Fundo de caixa (MT)</label>
        <input id="float" autoFocus inputMode="decimal" placeholder="0,00" value={v} onChange={(e) => setV(e.target.value)}
          className="num mt-1 h-16 w-full rounded-xl border border-base bg-surface-2 px-4 text-right text-3xl" />
        {error && <p role="alert" className="mt-3 rounded-lg bg-brand-50 p-3 text-sm text-brand-800">{error}</p>}
        <button disabled={busy} className="mt-5 flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-brand-700 text-lg font-semibold text-white disabled:opacity-40">
          {busy ? <Loader2 className="animate-spin" /> : <LockOpen />} Abrir caixa
        </button>
        <button type="button" onClick={onLock} className="mt-3 h-12 w-full text-sm text-muted underline">Trocar de operador</button>
        {onKitchen && <button type="button" onClick={onKitchen} className="h-12 w-full text-sm text-muted underline">Este equipamento é o ecrã da cozinha</button>}
      </form>
    </div>
  );
}

/** Resultado do último fecho: o operador vê logo se a caixa bateu certo. */
function ZResult({ z }: { z: CashReport }) {
  const d = new D(z.difference ?? 0);
  const tone = d.isZero() ? 'bg-green-50 text-green-900 dark:bg-green-950 dark:text-green-100' : 'bg-amber-100 text-amber-900';
  return (
    <div role="status" className={`mb-5 rounded-xl p-4 ${tone}`}>
      <div className="font-semibold">Caixa fechada — relatório Z n.º {z.zNumber}</div>
      <div className="num mt-1 text-2xl font-bold">{d.isZero() ? 'Caixa certa' : d.isNegative() ? `Falta ${formatMoney(d.abs())} MT` : `Sobra ${formatMoney(d)} MT`}</div>
      <div className="text-sm">Contado {formatMoney(z.countedCash ?? 0)} · esperado {formatMoney(z.expectedCash)}</div>
    </div>
  );
}
